const KEY = 'reloadedForNewBuildAt'
/** Background loads in flight (see loadQuietly). */
let quietLoads = 0
/** At most one automatic reload per minute, so a broken deploy can't cause a reload loop. */
export const RELOAD_COOLDOWN_MS = 60_000

/**
 * After a deploy, a page loaded from the previous build can request code chunks that no longer
 * exist (Vite fires `vite:preloadError`). Reload to pick up the new build, unless we already
 * reloaded for this reason within the last minute; then let the error surface instead. Not
 * during a background load (loadQuietly): nobody is waiting for it. Returns whether it reloaded.
 */
export function reloadOnStaleBuild(
  event: Event,
  now = Date.now(),
  reload = () => window.location.reload(),
): boolean {
  if (quietLoads > 0) return false
  let last: number
  try {
    last = Number(sessionStorage.getItem(KEY)) || 0
  } catch {
    return false // storage unavailable: don't risk a loop
  }
  if (now - last < RELOAD_COOLDOWN_MS) return false

  try {
    sessionStorage.setItem(KEY, String(now))
  } catch {
    return false
  }
  event.preventDefault()
  reload()
  return true
}

/**
 * Loads code nobody is waiting for yet (a preload). If it fails, for example on a network
 * blip, it just fails: the page isn't reloaded under the user, and the code loads again
 * when it's needed. (Vite fires `vite:preloadError` before the load rejects.)
 */
export async function loadQuietly<T>(load: () => Promise<T>): Promise<T> {
  quietLoads++
  try {
    return await load()
  } finally {
    quietLoads--
  }
}
